import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import MultiSelectDropdown from '../../dropdowns/MultiSelectDropdownNew';
import MultiSelectDropdownNoCreate from '../../dropdowns/MultiSelectDropdownNoCreate';
import { toast } from 'sonner';
import { FundraisingFlagConfigurationTabProps } from './types';

export default function FundraisingFlagConfiguration(props: FundraisingFlagConfigurationTabProps) {
  const {
    formData,
    updateFormData,
    availableForms,
    existingDomainSuggestions,
    isCharityStackInternalURL,
    handleFieldFocus,
    isFieldInvalid,
    invalidFields,
    setInvalidFields,
  } = props;

  const { elementName, allowedDomains, disallowedDomains, connectedForms } = formData;

  const setDisallowedDomains = (domains: string[]) => {
    updateFormData('disallowedDomains', domains);
    handleFieldFocus('configuration-disallowedDomains');
  };

  return (
    <>
      {/* Element Name */}
      <div className="space-y-0">
        <Label
          htmlFor="element-name"
          className="font-inter text-base font-medium leading-6 text-ink-secondary"
        >
          Element Name
        </Label>
        <Input
          id="element-name"
          value={elementName}
          onChange={(e) => {
            updateFormData('elementName', e.target.value);
          }}
          onFocus={() => handleFieldFocus('configuration-elementName')}
          className={`h-[28px] flex-shrink-0 self-stretch ${
            isFieldInvalid('elementName') ? 'border-red-500' : ''
          }`}
        />
      </div>

      {/* Form */}
      <div className="space-y-0">
        <Label className="font-inter text-base font-medium leading-6 text-ink-secondary">Form</Label>
        <div>
          <MultiSelectDropdownNoCreate
            height={28}
            options={(availableForms || [])
              .sort((a, b) => a.formName.localeCompare(b.formName))
              .map((form) => ({
                value: form.formName,
                label: form.formName,
              }))}
            setItems={(items) => {
              // Only allow single selection for Fundraising Flag
              updateFormData('connectedForms', items[0] ? [items[0]] : []);
              updateFormData(
                'formIDs',
                items[0]
                  ? [availableForms.find((form) => form.formName === items[0])?.formID || '']
                  : [],
              );
              handleFieldFocus('configuration-connectedForms');
            }}
            items={connectedForms && connectedForms.length > 0 ? [connectedForms[0]] : []}
            error={Boolean(isFieldInvalid('connectedForms'))}
          />
        </div>
      </div>

      {/* Domain Allow List */}
      <div className="space-y-1">
        <Label className="font-inter text-base font-medium leading-6 text-ink-secondary">
          Domain Allow List
        </Label>
        <div>
          <MultiSelectDropdown
            options={[...existingDomainSuggestions.whitelist]
              .filter((v, i, a) => a.indexOf(v) === i)
              .filter((domain) => !isCharityStackInternalURL(domain))
              .map((domain) => ({ value: domain, label: domain }))}
            setItems={(items) => {
              // Filter out CharityStack internal URLs
              const filteredItems = items.filter((item) => !isCharityStackInternalURL(item));

              // Show error if user tried to add blocked domain
              if (filteredItems.length < items.length) {
                toast.error(
                  'Cannot add CharityStack internal URLs (donate, attend, dashboard pages) to whitelist',
                );
              }

              updateFormData('allowedDomains', filteredItems);
              handleFieldFocus('configuration-allowedDomains');
            }}
            items={allowedDomains}
            error={Boolean(isFieldInvalid('allowedDomains'))}
          />
        </div>
      </div>

      {/* Domain Disallow List */}
      <div className="space-y-1">
        <Label className="font-inter text-base font-medium leading-6 text-ink-secondary">
          Domain Disallow List
        </Label>
        <MultiSelectDropdown
          height={28}
          options={[...existingDomainSuggestions.blacklist]
            .filter((v, i, a) => a.indexOf(v) === i)
            .map((domain) => ({ value: domain, label: domain }))}
          setItems={setDisallowedDomains}
          items={disallowedDomains}
        />
      </div>
    </>
  );
}
